import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import TopMenu from '../../contexts/TopMenu';
import '../../styles/ProductsManager.css';

const PAGE_SIZE = 24;

// Fallback taxonomy mapping if API taxonomy fetch is delayed
const DEFAULT_TAXONOMY = {
  top_wear: [
    { name: 'Tops', is_ethnic: false },
    { name: 'Shirts', is_ethnic: false },
    { name: 'Tshirts', is_ethnic: false },
    { name: 'Sweatshirts', is_ethnic: false },
    { name: 'Kurtas', is_ethnic: true },
    { name: 'Kurtis', is_ethnic: true },
    { name: 'Bodysuit', is_ethnic: false },
    { name: 'Camisoles', is_ethnic: false },
    { name: 'Tunics', is_ethnic: true }
  ],
  bottom_wear: [
    { name: 'Trousers', is_ethnic: false },
    { name: 'Skirts', is_ethnic: false },
    { name: 'Jeans', is_ethnic: false },
    { name: 'Track Pants', is_ethnic: false },
    { name: 'Shorts', is_ethnic: false },
    { name: 'Leggings', is_ethnic: false },
    { name: 'Harem Pants', is_ethnic: true },
    { name: 'Palazzos', is_ethnic: true },
    { name: 'Pyjamas', is_ethnic: true },
    { name: 'Capris', is_ethnic: false }
  ],
  one_piece: [
    { name: 'Dresses', is_ethnic: false },
    { name: 'Co-Ords', is_ethnic: false },
    { name: 'Swimwear', is_ethnic: false },
    { name: 'Sarees', is_ethnic: true },
    { name: 'Clothing Set', is_ethnic: false },
    { name: 'Kurta Sets', is_ethnic: true },
    { name: 'Jumpsuit', is_ethnic: false },
    { name: 'Ethnic Dresses', is_ethnic: true },
    { name: 'Suits', is_ethnic: false },
    { name: 'Lehenga Choli', is_ethnic: true }
  ],
  outerwear: [
    { name: 'Sweaters', is_ethnic: false },
    { name: 'Jackets', is_ethnic: false },
    { name: 'Blazers', is_ethnic: false },
    { name: 'Shrug', is_ethnic: false },
    { name: 'Coats', is_ethnic: false },
    { name: 'Shackets', is_ethnic: false },
    { name: 'Nehru Jackets', is_ethnic: true }
  ],
  accessories: [
    { name: 'Handbags', is_ethnic: false },
    { name: 'Earrings', is_ethnic: true },
    { name: 'Necklace and Chains', is_ethnic: true },
    { name: 'Wallets', is_ethnic: false },
    { name: 'Hair Accessory', is_ethnic: false },
    { name: 'Ring', is_ethnic: false },
    { name: 'Sunglasses', is_ethnic: false },
    { name: 'Scarves', is_ethnic: false },
    { name: 'Bracelet', is_ethnic: false },
    { name: 'Clutches', is_ethnic: false },
    { name: 'Caps', is_ethnic: false },
    { name: 'Charms', is_ethnic: false },
    { name: 'Pendant', is_ethnic: false },
    { name: 'Anklet', is_ethnic: true },
    { name: 'Belts', is_ethnic: false },
    { name: 'Bangle', is_ethnic: true },
    { name: 'Dupatta', is_ethnic: true },
    { name: 'Laptop Bag', is_ethnic: false },
    { name: 'Stoles', is_ethnic: false },
    { name: 'Brooch', is_ethnic: false },
    { name: 'Messenger Bag', is_ethnic: false },
    { name: 'Watches', is_ethnic: false }
  ],
  footwear: [
    { name: 'Casual Shoes', is_ethnic: false },
    { name: 'Sports Shoes', is_ethnic: false },
    { name: 'Boots', is_ethnic: false },
    { name: 'Heels', is_ethnic: false },
    { name: 'Flats', is_ethnic: false },
    { name: 'Sandals', is_ethnic: false },
    { name: 'Formal Shoes', is_ethnic: false },
    { name: 'Ballerinas', is_ethnic: false }
  ]
};

const SUPER_CATEGORY_ICONS = {
  top_wear: '👕',
  bottom_wear: '👖',
  one_piece: '👗',
  outerwear: '🧥',
  accessories: '👜',
  footwear: '👟',
  uncategorized: '❓'
};

export const getProductImageUrl = (product) => {
  if (!product) return '';
  if (product.top_image && typeof product.top_image === 'string' && product.top_image.trim()) {
    return product.top_image.trim();
  }
  if (Array.isArray(product.images) && product.images.length > 0) {
    for (const img of product.images) {
      if (typeof img === 'string' && img.trim()) return img.trim();
      if (typeof img === 'object' && img !== null) {
        const u = img.url || img.src || img.image_url;
        if (typeof u === 'string' && u.trim()) return u.trim();
      }
    }
  }
  const single = product.image || product.featured_image || product.image_url || product.top_image_source_url;
  if (typeof single === 'string' && single.trim()) return single.trim();
  if (typeof single === 'object' && single !== null) {
    const u = single.url || single.src || single.image_url;
    if (typeof u === 'string' && u.trim()) return u.trim();
  }
  return '';
};

export const CURRENCY_SYMBOLS = {
  INR: '₹',
  USD: '$',
  GBP: '£',
  EUR: '€',
  AED: 'AED ',
  CAD: 'CA$',
  AUD: 'AU$',
  SGD: 'S$',
  JPY: '¥',
};

export const getProductCurrency = (product) => {
  if (!product) return 'INR';
  const curr = product.default_currency || product.currency || product.defaultCurrency;
  if (curr && typeof curr === 'string' && curr.trim()) {
    return curr.trim().toUpperCase();
  }
  if (Array.isArray(product.variants) && product.variants.length > 0) {
    const vCurr = product.variants[0]?.default_currency || product.variants[0]?.currency;
    if (vCurr && typeof vCurr === 'string' && vCurr.trim()) {
      return vCurr.trim().toUpperCase();
    }
  }
  return 'INR';
};

export const formatProductPrice = (price, currencyCode = 'INR') => {
  if (price == null || price === '') return null;
  const num = Number(price);
  if (isNaN(num)) return price;
  const curr = (currencyCode || 'INR').trim().toUpperCase() || 'INR';
  const symbol = CURRENCY_SYMBOLS[curr] || `${curr} `;
  const locale = curr === 'INR' ? 'en-IN' : 'en-US';
  return `${symbol}${num.toLocaleString(locale)}`;
};

export const getProductPrice = (product) => {
  if (!product) return null;
  if (product.price) return product.price;
  if (Array.isArray(product.variants) && product.variants.length > 0) {
    const vPrice = product.variants[0]?.price;
    if (vPrice) return vPrice;
  }
  return null;
};

export const getProductImagesList = (product) => {
  if (!product) return [];
  const list = [];
  const seenUrls = new Set();

  const addUrl = (url, id) => {
    if (typeof url === 'string' && url.trim() && !seenUrls.has(url.trim())) {
      seenUrls.add(url.trim());
      list.push({ url: url.trim(), id: id || url.trim() });
    }
  };

  if (Array.isArray(product.images) && product.images.length > 0) {
    for (const img of product.images) {
      if (typeof img === 'string') {
        addUrl(img);
      } else if (typeof img === 'object' && img !== null) {
        const u = img.url || img.src || img.image_url;
        addUrl(u, img.id);
      }
    }
  }

  addUrl(product.top_image);
  addUrl(product.image);
  addUrl(product.featured_image);
  addUrl(product.image_url);
  addUrl(product.top_image_source_url);

  return list;
};

function ProductsManager({ email, onLogout }) {
  const { superCategory: rawRouteSuperCategory, category: rawRouteCategory } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const routeSuperCategory = useMemo(
    () => (rawRouteSuperCategory ? decodeURIComponent(rawRouteSuperCategory) : null),
    [rawRouteSuperCategory]
  );
  const routeCategory = useMemo(
    () => (rawRouteCategory ? decodeURIComponent(rawRouteCategory) : null),
    [rawRouteCategory]
  );

  // Tree & Taxonomy State
  const [treeData, setTreeData] = useState([]);
  const [taxonomy, setTaxonomy] = useState(DEFAULT_TAXONOMY);
  const [totalProductsCount, setTotalProductsCount] = useState(0);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [treeLoading, setTreeLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState(() => ({
    top_wear: true,
    ...(rawRouteSuperCategory ? { [decodeURIComponent(rawRouteSuperCategory)]: true } : {}),
  }));

  // Filter States
  const [selectedSuperCategory, setSelectedSuperCategory] = useState(routeSuperCategory);
  const [selectedCategory, setSelectedCategory] = useState(routeCategory);

  useEffect(() => {
    setSelectedSuperCategory(routeSuperCategory || null);
    setSelectedCategory(routeCategory || null);
    if (routeSuperCategory) {
      setExpandedNodes((prev) => ({
        ...prev,
        [routeSuperCategory]: true,
      }));
    }
  }, [routeSuperCategory, routeCategory]);

  const navigateToCategoryPath = useCallback(
    (scKey, catName) => {
      let targetPath = '/products';
      if (scKey) {
        targetPath = `/products/${encodeURIComponent(scKey)}`;
        if (catName) {
          targetPath += `/${encodeURIComponent(catName)}`;
        }
      }
      if (location.pathname !== targetPath) {
        navigate(`${targetPath}${location.search || ''}`);
      }
    },
    [navigate, location.pathname, location.search]
  );
  const [selectedGender, setSelectedGender] = useState('all'); // 'all', 'men', 'women'
  const [selectedEthnicFilter, setSelectedEthnicFilter] = useState('all'); // 'all', 'true', 'false'
  const [selectedFlatLayFilter, setSelectedFlatLayFilter] = useState('true'); // 'true' (default on), 'false', 'all'
  const [selectedVendor, setSelectedVendor] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'table'

  // Scroll sentinel ref for infinite scroll
  const sentinelRef = useRef(null);

  // Back to top button state
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Products Data State
  const [products, setProducts] = useState([]);
  const [vendorsList, setVendorsList] = useState([]);
  const sortedVendorsList = useMemo(() => {
    return [...vendorsList].filter(Boolean).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
  }, [vendorsList]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    has_next: false,
    has_previous: false,
  });
  const [productsLoading, setProductsLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState(new Set());
  const [selectAllStage, setSelectAllStage] = useState('none'); // 'none' | 'page' | 'all'
  const selectAllStageRef = useRef('none');
  const [selectingAllPages, setSelectingAllPages] = useState(false);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null); // single product or null for bulk
  const [modalSuperCategory, setModalSuperCategory] = useState('top_wear');
  const [modalCategory, setModalCategory] = useState('');
  const [modalGender, setModalGender] = useState('female');
  const [modalIsEthnic, setModalIsEthnic] = useState(false);
  const [savingCategory, setSavingCategory] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  // Modal Gallery state
  const [galleryModalProduct, setGalleryModalProduct] = useState(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  // Discontinue / Mark Active state
  const [togglingProductId, setTogglingProductId] = useState(null);

  // Monitor scroll for Back to Top button
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const showToast = (message, type = 'success') => {
    setToastMessage({ message, type });
    setTimeout(() => {
      setToastMessage(null);
    }, 4000);
  };

  // Fetch Category Tree
  const fetchCategoryTree = useCallback(async () => {
    try {
      setTreeLoading(true);
      const treeParams = { status: 'active' };
      if (selectedFlatLayFilter !== 'all') {
        treeParams.flat_lay = selectedFlatLayFilter === 'true';
      }
      const res = await adminApi.getProductCategoryTree(treeParams);
      if (res?.data) {
        setTreeData(res.data.tree || []);
        setTotalProductsCount(res.data.total_products || 0);
        setUncategorizedCount(res.data.uncategorized_count || 0);
        if (res.data.taxonomy && Object.keys(res.data.taxonomy).length > 0) {
          setTaxonomy(res.data.taxonomy);
        }
      }
    } catch (err) {
      console.error('Failed to fetch category tree:', err);
    } finally {
      setTreeLoading(false);
    }
  }, [selectedFlatLayFilter]);

  useEffect(() => {
    fetchCategoryTree();
  }, [fetchCategoryTree]);

  // Initial and filtered fetch
  const fetchInitialProducts = useCallback(async () => {
    try {
      setProductsLoading(true);
      setSelectedProductIds(new Set());
      setSelectAllStage('none');
      selectAllStageRef.current = 'none';
      const params = {
        page: 1,
        limit: PAGE_SIZE,
        status: 'active',
      };

      if (selectedSuperCategory) params.super_category = selectedSuperCategory;
      if (selectedCategory) params.category = selectedCategory;
      if (selectedGender !== 'all') params.gender = selectedGender;
      if (selectedVendor !== 'all') params.vendor = selectedVendor;
      if (selectedEthnicFilter !== 'all') params.is_ethnic = selectedEthnicFilter === 'true';
      if (selectedFlatLayFilter !== 'all') params.flat_lay = selectedFlatLayFilter === 'true';
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await adminApi.getAdminProducts(params);
      if (res?.data) {
        const fetchedProducts = res.data.products || [];
        setProducts(fetchedProducts);
        setVendorsList(res.data.vendors || []);
        const pag = res.data.pagination || {
          current_page: 1,
          total_pages: 1,
          total_items: fetchedProducts.length,
        };
        setPagination(pag);
        setPage(1);
        setHasMore((pag.total_pages || 1) > 1 && fetchedProducts.length < (pag.total_items || 0));
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
      showToast('Error loading products: ' + (err.message || 'Server error'), 'error');
    } finally {
      setProductsLoading(false);
    }
  }, [selectedSuperCategory, selectedCategory, selectedGender, selectedVendor, selectedEthnicFilter, selectedFlatLayFilter, debouncedSearch]);

  useEffect(() => {
    fetchInitialProducts();
  }, [fetchInitialProducts]);

  // Load next batch on scroll
  const loadMoreProducts = useCallback(async () => {
    if (productsLoading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const params = {
        page: nextPage,
        limit: PAGE_SIZE,
        status: 'active',
      };

      if (selectedSuperCategory) params.super_category = selectedSuperCategory;
      if (selectedCategory) params.category = selectedCategory;
      if (selectedGender !== 'all') params.gender = selectedGender;
      if (selectedVendor !== 'all') params.vendor = selectedVendor;
      if (selectedEthnicFilter !== 'all') params.is_ethnic = selectedEthnicFilter === 'true';
      if (selectedFlatLayFilter !== 'all') params.flat_lay = selectedFlatLayFilter === 'true';
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await adminApi.getAdminProducts(params);
      if (res?.data && res.data.products) {
        const newItems = res.data.products;
        setProducts((prev) => {
          const existingIds = new Set(prev.map((p) => p._id || p.handle || p.external_product_id));
          const filteredNew = newItems.filter((p) => !existingIds.has(p._id || p.handle || p.external_product_id));
          const combined = [...prev, ...filteredNew];
          const totalItems = res.data.pagination?.total_items ?? pagination.total_items;
          const totalPages = res.data.pagination?.total_pages ?? pagination.total_pages;
          setHasMore(nextPage < totalPages && combined.length < totalItems);
          return combined;
        });
        if (selectAllStageRef.current === 'all') {
          setSelectedProductIds((prev) => {
            const next = new Set(prev);
            newItems.forEach((p) => {
              const pid = p._id || p.handle || p.external_product_id;
              if (pid) next.add(pid);
            });
            return next;
          });
        }
        setPage(nextPage);
        if (res.data.pagination) {
          setPagination(res.data.pagination);
        }
      }
    } catch (err) {
      console.error('Failed to load more products:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [productsLoading, loadingMore, hasMore, page, selectedSuperCategory, selectedCategory, selectedGender, selectedVendor, selectedEthnicFilter, selectedFlatLayFilter, debouncedSearch, pagination.total_items, pagination.total_pages]);

  // Setup IntersectionObserver for infinite scrolling
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || productsLoading || loadingMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreProducts();
        }
      },
      {
        root: null,
        rootMargin: '500px', // Pre-fetch 500px before reaching the bottom
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);
    return () => {
      if (sentinel) observer.unobserve(sentinel);
    };
  }, [hasMore, productsLoading, loadingMore, loadMoreProducts]);

  // Modal Gallery interactions
  const openGalleryModal = (product, initialIndex = 0) => {
    setGalleryModalProduct(product);
    setActiveImageIndex(initialIndex);
  };

  const closeGalleryModal = () => {
    setGalleryModalProduct(null);
    setActiveImageIndex(0);
  };

  const galleryImages = useMemo(() => {
    return getProductImagesList(galleryModalProduct);
  }, [galleryModalProduct]);

  const handleNextImage = useCallback(() => {
    if (!galleryImages.length) return;
    setActiveImageIndex((prev) => (prev + 1) % galleryImages.length);
  }, [galleryImages]);

  const handlePrevImage = useCallback(() => {
    if (!galleryImages.length) return;
    setActiveImageIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  }, [galleryImages]);

  const currentGalleryProductIndex = galleryModalProduct
    ? products.findIndex((p) => {
        const pId = p._id || p.handle || p.external_product_id;
        const targetId = galleryModalProduct._id || galleryModalProduct.handle || galleryModalProduct.external_product_id;
        return pId === targetId;
      })
    : -1;

  const handlePrevProduct = useCallback(() => {
    if (currentGalleryProductIndex > 0) {
      setGalleryModalProduct(products[currentGalleryProductIndex - 1]);
      setActiveImageIndex(0);
    }
  }, [currentGalleryProductIndex, products]);

  const handleNextProduct = useCallback(() => {
    if (currentGalleryProductIndex >= 0 && currentGalleryProductIndex < products.length - 1) {
      setGalleryModalProduct(products[currentGalleryProductIndex + 1]);
      setActiveImageIndex(0);
    } else if (hasMore && !loadingMore) {
      loadMoreProducts();
    }
  }, [currentGalleryProductIndex, products, hasMore, loadingMore, loadMoreProducts]);

  // Keyboard navigation for image modal and product switching
  useEffect(() => {
    if (!galleryModalProduct) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeGalleryModal();
      if (e.key === 'ArrowRight' && !e.shiftKey) handleNextImage();
      if (e.key === 'ArrowLeft' && !e.shiftKey) handlePrevImage();
      if ((e.key === 'ArrowRight' && e.shiftKey) || e.key === 'PageDown' || e.key === ']') handleNextProduct();
      if ((e.key === 'ArrowLeft' && e.shiftKey) || e.key === 'PageUp' || e.key === '[') handlePrevProduct();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [galleryModalProduct, handleNextImage, handlePrevImage, handleNextProduct, handlePrevProduct]);

  // Handle Tree Node Expand / Collapse
  const toggleNodeExpand = (nodeKey) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeKey]: !prev[nodeKey]
    }));
  };

  // Filter selection from tree
  const handleSelectSuperCategory = (scKey) => {
    if (selectedSuperCategory === scKey && !selectedCategory) {
      // Toggle off if already selected
      setSelectedSuperCategory(null);
      navigateToCategoryPath(null, null);
    } else {
      setSelectedSuperCategory(scKey);
      setSelectedCategory(null);
      setExpandedNodes(prev => ({ ...prev, [scKey]: true }));
      navigateToCategoryPath(scKey, null);
    }
    setPage(1);
    setSelectedProductIds(new Set());
  };

  const handleSelectCategory = (scKey, catName) => {
    setSelectedSuperCategory(scKey);
    setSelectedCategory(catName);
    setExpandedNodes(prev => ({ ...prev, [scKey]: true }));
    navigateToCategoryPath(scKey, catName);
    setPage(1);
    setSelectedProductIds(new Set());
  };

  const handleClearFilters = () => {
    setSelectedSuperCategory(null);
    setSelectedCategory(null);
    setSelectedGender('all');
    setSelectedEthnicFilter('all');
    setSelectedFlatLayFilter('true');
    setSelectedVendor('all');
    setSearchTerm('');
    setDebouncedSearch('');
    setPage(1);
    setSelectedProductIds(new Set());
    setSelectAllStage('none');
    selectAllStageRef.current = 'none';
    if (location.pathname !== '/products') {
      navigate('/products');
    }
  };

  // Open Edit Modal for Single Product
  const openEditModalForProduct = (product) => {
    setEditingProduct(product);
    const va = product.visual_attributes || {};
    const gt = va.garment_type || {};
    const g = va.gender?.value || (product.gender ? product.gender : 'female');

    const sc = (gt.primary || 'top_wear').toLowerCase();
    const cat = gt.category || '';
    const isEth = Boolean(product.is_ethnic);

    setModalSuperCategory(sc);
    setModalCategory(cat);
    setModalGender(g === 'male' || g === 'men' ? 'men' : 'women');
    setModalIsEthnic(isEth);
    setEditModalOpen(true);
  };

  // Open Edit Modal for Bulk Selection
  const openBulkEditModal = () => {
    if (selectedProductIds.size === 0) return;
    setEditingProduct(null);
    setModalSuperCategory(selectedSuperCategory && selectedSuperCategory !== 'uncategorized' ? selectedSuperCategory : 'top_wear');
    setModalCategory('');
    setModalGender(selectedGender !== 'all' ? selectedGender : 'women');
    setModalIsEthnic(selectedEthnicFilter === 'true');
    setEditModalOpen(true);
  };

  // Categories available for currently selected modal super category
  const modalAvailableCategories = useMemo(() => {
    const genderKey = modalGender === 'men' ? 'men' : 'women';
    const taxList = (taxonomy[genderKey] && taxonomy[genderKey][modalSuperCategory]) ||
      (taxonomy[modalSuperCategory]) ||
      (DEFAULT_TAXONOMY[modalSuperCategory]) || [];

    const list = taxList.map(item => (typeof item === 'string' ? { name: item, is_ethnic: false } : item));
    return list;
  }, [taxonomy, modalGender, modalSuperCategory]);

  // When super category changes in modal, auto-pick first category if current is not in list
  const handleModalSuperCategoryChange = (newSc) => {
    setModalSuperCategory(newSc);
    const genderKey = modalGender === 'men' ? 'men' : 'women';
    const list = (taxonomy[genderKey] && taxonomy[genderKey][newSc]) ||
      (DEFAULT_TAXONOMY[newSc]) || [];
    if (list.length > 0) {
      const first = typeof list[0] === 'string' ? list[0] : list[0].name;
      setModalCategory(first);
      const isEth = typeof list[0] === 'object' ? Boolean(list[0].is_ethnic) : false;
      setModalIsEthnic(isEth);
    } else {
      setModalCategory('');
    }
  };

  // When category changes in modal, auto-detect ethnic flag if defined in taxonomy
  const handleModalCategoryChange = (newCat) => {
    setModalCategory(newCat);
    const matched = modalAvailableCategories.find(c => c.name.toLowerCase() === newCat.toLowerCase());
    if (matched && matched.is_ethnic !== undefined) {
      setModalIsEthnic(matched.is_ethnic);
    }
  };

  // Save Category Changes
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    if (!modalCategory) {
      showToast('Please select or specify a category name.', 'error');
      return;
    }

    try {
      setSavingCategory(true);
      const payload = {
        super_category: modalSuperCategory,
        category: modalCategory,
        gender: modalGender,
        is_ethnic: modalIsEthnic
      };

      if (editingProduct) {
        // Single update
        const pid = editingProduct._id || editingProduct.handle || editingProduct.external_product_id;
        const res = await adminApi.updateProductCategory(pid, payload);
        if (res?.data) {
          // Optimistically update product in local list
          setProducts(prev => prev.map(p => {
            const currentPid = p._id || p.handle || p.external_product_id;
            if (currentPid === pid) {
              return {
                ...p,
                visual_attributes: {
                  ...p.visual_attributes,
                  garment_type: {
                    ...p.visual_attributes?.garment_type,
                    primary: modalSuperCategory,
                    category: modalCategory,
                  },
                  gender: {
                    ...p.visual_attributes?.gender,
                    value: modalGender === 'men' ? 'male' : 'female'
                  }
                },
                is_ethnic: modalIsEthnic
              };
            }
            return p;
          }));
          showToast(`Updated "${editingProduct.title}" category to ${modalCategory}`);
        }
      } else {
        // Bulk update
        const pids = Array.from(selectedProductIds);
        const res = await adminApi.bulkUpdateProductCategory({
          product_ids: pids,
          ...payload
        });
        showToast(res.message || `Updated ${pids.length} products`);
        setSelectedProductIds(new Set());
        fetchInitialProducts();
      }

      setEditModalOpen(false);
      // Refresh tree counts in background
      fetchCategoryTree();
    } catch (err) {
      console.error('Save category error:', err);
      showToast('Failed to save category: ' + (err.message || 'Server error'), 'error');
    } finally {
      setSavingCategory(false);
    }
  };

  // Discontinue / Mark Active product status toggle
  const handleToggleProductStatus = async (product, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const pid = product?._id || product?.id;
    if (!pid) return;

    const isCurrentlyDiscontinued = Boolean(
      product.discontinued ||
      String(product.status || '').toLowerCase() === 'inactive' ||
      String(product.status || '').toLowerCase() === 'discontinued'
    );
    const targetDiscontinued = !isCurrentlyDiscontinued;
    const targetStatus = targetDiscontinued ? 'inactive' : 'active';

    setTogglingProductId(pid);
    try {
      await adminApi.toggleProductStatus(pid, {
        discontinued: targetDiscontinued,
        status: targetStatus,
      });

      // Update in products list
      setProducts((prev) =>
        prev.map((p) => {
          const pId = p._id || p.id;
          if (pId === pid) {
            return {
              ...p,
              discontinued: targetDiscontinued,
              status: targetStatus,
            };
          }
          return p;
        })
      );

      // Update in galleryModalProduct if modal is currently open
      setGalleryModalProduct((prev) => {
        if (!prev) return prev;
        const prevId = prev._id || prev.id;
        if (prevId === pid) {
          return {
            ...prev,
            discontinued: targetDiscontinued,
            status: targetStatus,
          };
        }
        return prev;
      });

      showToast(
        targetDiscontinued
          ? `Product marked as discontinued (inactive)`
          : `Product marked as active`
      );
    } catch (err) {
      console.error('Failed to update product status:', err);
      showToast('Failed to update status: ' + (err.message || 'Server error'), 'error');
    } finally {
      setTogglingProductId(null);
    }
  };

  // Toggle selection for bulk
  const toggleSelectProduct = (pid) => {
    setSelectAllStage('none');
    selectAllStageRef.current = 'none';
    setSelectedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const pageProductIds = useMemo(() => {
    return products.map(p => p._id || p.handle || p.external_product_id).filter(Boolean);
  }, [products]);

  const totalMatchingItems = pagination.total_items || products.length;
  const isCurrentPageSelected =
    pageProductIds.length > 0 && pageProductIds.every(id => selectedProductIds.has(id));
  const isAllPagesSelected =
    selectAllStage === 'all' ||
    (isCurrentPageSelected && selectedProductIds.size >= totalMatchingItems && totalMatchingItems > 0);

  const handleSelectAllTap = async () => {
    if (products.length === 0 || selectingAllPages) return;

    const currentPageIds = products.map(p => p._id || p.handle || p.external_product_id).filter(Boolean);
    const totalCount = pagination.total_items || currentPageIds.length;
    const hasMultiplePages = totalCount > currentPageIds.length;

    // 1st tap: select current single page only
    if (selectAllStageRef.current === 'none' && !isCurrentPageSelected) {
      setSelectedProductIds(new Set(currentPageIds));
      setSelectAllStage('page');
      selectAllStageRef.current = 'page';
      if (hasMultiplePages) {
        showToast(
          `Selected ${currentPageIds.length} products on this page. Tap "Select All" again to select all ${totalCount.toLocaleString()} across all pages.`
        );
      } else {
        showToast(`Selected all ${currentPageIds.length} products.`);
      }
      return;
    }

    // 2nd tap: select all pages matching current filter (if multiple pages exist)
    if (
      (selectAllStageRef.current === 'page' || (isCurrentPageSelected && !isAllPagesSelected)) &&
      hasMultiplePages
    ) {
      selectAllStageRef.current = 'all';
      setSelectAllStage('all');
      setSelectingAllPages(true);
      try {
        const params = {
          page: 1,
          limit: Math.max(totalCount, 10000),
          status: 'active',
          ids_only: true,
        };
        if (selectedSuperCategory) params.super_category = selectedSuperCategory;
        if (selectedCategory) params.category = selectedCategory;
        if (selectedGender !== 'all') params.gender = selectedGender;
        if (selectedVendor !== 'all') params.vendor = selectedVendor;
        if (selectedEthnicFilter !== 'all') params.is_ethnic = selectedEthnicFilter === 'true';
        if (selectedFlatLayFilter !== 'all') params.flat_lay = selectedFlatLayFilter === 'true';
        if (debouncedSearch) params.search = debouncedSearch;

        const res = await adminApi.getAdminProducts(params);
        const fetchedIds = res?.data?.product_ids
          ? res.data.product_ids
          : (res?.data?.products || [])
              .map(p => p._id || p.handle || p.external_product_id)
              .filter(Boolean);

        const combinedIds = new Set([...currentPageIds, ...fetchedIds]);
        setSelectedProductIds(combinedIds);
        showToast(`Selected all ${combinedIds.size.toLocaleString()} products across all pages.`);
      } catch (err) {
        console.error('Failed to select all pages:', err);
        selectAllStageRef.current = 'page';
        setSelectAllStage('page');
        showToast('Failed to select all pages: ' + (err.message || 'Server error'), 'error');
      } finally {
        setSelectingAllPages(false);
      }
      return;
    }

    // Subsequent tap when all pages (or single page) are already selected: clear selection
    setSelectedProductIds(new Set());
    setSelectAllStage('none');
    selectAllStageRef.current = 'none';
  };

  const toggleSelectAllPage = () => {
    if (isCurrentPageSelected) {
      setSelectedProductIds(new Set());
      setSelectAllStage('none');
      selectAllStageRef.current = 'none';
    } else {
      const allIds = new Set(products.map(p => p._id || p.handle || p.external_product_id).filter(Boolean));
      setSelectedProductIds(allIds);
      setSelectAllStage('page');
      selectAllStageRef.current = 'page';
    }
  };

  return (
    <div className="dashboard-root products-manager-root">
      <TopMenu pageTitle="Product Category & Taxonomy Manager" email={email} onLogout={onLogout} />

      {/* Toast Notification */}
      {toastMessage && (
        <div className={`pm-toast pm-toast-${toastMessage.type}`}>
          <span>{toastMessage.message}</span>
        </div>
      )}

      <div className="pm-container">
        {/* ========================================================= */}
        {/* LEFT SIDEBAR: CATEGORY TAXONOMY TREE & QUICK FILTERS     */}
        {/* ========================================================= */}
        <aside className="pm-sidebar">
          <div className="pm-sidebar-header">
            <h3>Category Hierarchy</h3>
            <span className="pm-total-badge">{totalProductsCount.toLocaleString()} items</span>
          </div>

          {/* Quick Filters */}
          <div className="pm-quick-filters">
            <button
              className={`pm-quick-btn ${!selectedSuperCategory && !selectedCategory ? 'active' : ''}`}
              onClick={handleClearFilters}
            >
              <span>🌐 All Active Items</span>
              <span className="pm-badge">{totalProductsCount.toLocaleString()}</span>
            </button>

            {uncategorizedCount > 0 && (
              <button
                className={`pm-quick-btn pm-btn-alert ${selectedSuperCategory === 'uncategorized' ? 'active' : ''}`}
                onClick={() => handleSelectSuperCategory('uncategorized')}
              >
                <span>⚠️ Uncategorized</span>
                <span className="pm-badge pm-badge-warning">{uncategorizedCount}</span>
              </button>
            )}
          </div>

          {/* Gender Filter Tabs */}
          <div className="pm-gender-tabs">
            <button
              className={`pm-gender-tab ${selectedGender === 'all' ? 'active' : ''}`}
              onClick={() => { setSelectedGender('all'); setPage(1); }}
            >
              All
            </button>
            <button
              className={`pm-gender-tab ${selectedGender === 'women' ? 'active' : ''}`}
              onClick={() => { setSelectedGender('women'); setPage(1); }}
            >
              Women
            </button>
            <button
              className={`pm-gender-tab ${selectedGender === 'men' ? 'active' : ''}`}
              onClick={() => { setSelectedGender('men'); setPage(1); }}
            >
              Men
            </button>
          </div>

          {/* Taxonomy Tree Nodes */}
          <div className="pm-tree-scroll">
            {treeLoading ? (
              <div className="pm-tree-loading">
                <div className="pm-spinner small"></div>
                <span>Loading taxonomy...</span>
              </div>
            ) : (
              <div className="pm-tree">
                {treeData.map(node => {
                  const isExpanded = Boolean(expandedNodes[node.key]);
                  const isNodeSelected = selectedSuperCategory === node.key && !selectedCategory;
                  const nodeIcon = SUPER_CATEGORY_ICONS[node.key] || '📁';
                  const nodeCount = selectedGender === 'men' ? node.men : (selectedGender === 'women' ? node.women : node.total);

                  return (
                    <div key={node.key} className="pm-tree-node">
                      <div className={`pm-node-header ${isNodeSelected ? 'selected' : ''}`}>
                        <button
                          className="pm-node-expander"
                          onClick={() => toggleNodeExpand(node.key)}
                          aria-label="Expand category"
                        >
                          {isExpanded ? '▾' : '▸'}
                        </button>
                        <div
                          className="pm-node-title-wrap"
                          onClick={() => handleSelectSuperCategory(node.key)}
                        >
                          <span className="pm-node-icon">{nodeIcon}</span>
                          <span className="pm-node-name">{node.label}</span>
                          <span className="pm-node-count">{nodeCount.toLocaleString()}</span>
                        </div>
                      </div>

                      {/* Sub-categories */}
                      {isExpanded && node.categories && node.categories.length > 0 && (
                        <div className="pm-tree-children">
                          {node.categories.map(cat => {
                            const isCatSelected = selectedSuperCategory === node.key && selectedCategory === cat.name;
                            const catCount = selectedGender === 'men' ? cat.men : (selectedGender === 'women' ? cat.women : cat.total);

                            return (
                              <div
                                key={cat.name}
                                className={`pm-child-node ${isCatSelected ? 'selected' : ''}`}
                                onClick={() => handleSelectCategory(node.key, cat.name)}
                              >
                                <span className="pm-child-bullet">•</span>
                                <span className="pm-child-name">{cat.name}</span>
                                {cat.is_ethnic && <span className="pm-mini-tag ethnic">Eth</span>}
                                <span className="pm-child-count">{catCount.toLocaleString()}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        {/* ========================================================= */}
        {/* MAIN EXPLORER: SEARCH, FILTERS, PRODUCT GRID & TABLE      */}
        {/* ========================================================= */}
        <main className="pm-main">
          {/* Sticky Header: Search Controls & Active Filters */}
          <div className="pm-sticky-header">
            {/* Top Control Bar */}
            <div className="pm-top-controls">
              <div className="pm-search-box">
                <span className="pm-search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search products by title, handle, brand or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button className="pm-search-clear" onClick={() => setSearchTerm('')}>✕</button>
                )}
              </div>

              <div className="pm-filter-selects">
                {/* Ethnic Filter */}
                <select
                  className="pm-select"
                  value={selectedEthnicFilter}
                  onChange={(e) => { setSelectedEthnicFilter(e.target.value); setPage(1); }}
                >
                  <option value="all">Style: All Types</option>
                  <option value="true">Ethnic Only</option>
                  <option value="false">Western Only</option>
                </select>

                {/* Flat Lays Filter */}
                <select
                  className="pm-select"
                  value={selectedFlatLayFilter}
                  onChange={(e) => { setSelectedFlatLayFilter(e.target.value); setPage(1); }}
                >
                  <option value="true">Flat Lays: Flat Lays Only</option>
                  <option value="false">Flat Lays: Non-Flat Lays</option>
                  <option value="all">Flat Lays: All</option>
                </select>

                {/* Vendor Filter */}
                <select
                  className="pm-select"
                  value={selectedVendor}
                  onChange={(e) => { setSelectedVendor(e.target.value); setPage(1); }}
                >
                  <option value="all">Brand: All Brands</option>
                  {sortedVendorsList.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>

                {/* View Mode Toggle */}
                <div className="pm-view-toggle">
                  <button
                    className={`pm-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid View"
                  >
                    ⊞
                  </button>
                  <button
                    className={`pm-toggle-btn ${viewMode === 'table' ? 'active' : ''}`}
                    onClick={() => setViewMode('table')}
                    title="Table View"
                  >
                    ☰
                  </button>
                </div>
              </div>
            </div>

            {/* Active Filter Pills Bar */}
            <div className="pm-active-bar">
              <div className="pm-active-pills">
                {selectedSuperCategory && (
                  <span className="pm-pill">
                    Super: {selectedSuperCategory}
                    <button onClick={() => { setSelectedSuperCategory(null); setSelectedCategory(null); navigateToCategoryPath(null, null); }}>✕</button>
                  </span>
                )}
                {selectedCategory && (
                  <span className="pm-pill highlight">
                    Category: {selectedCategory}
                    <button onClick={() => { setSelectedCategory(null); navigateToCategoryPath(selectedSuperCategory, null); }}>✕</button>
                  </span>
                )}
                {selectedGender !== 'all' && (
                  <span className="pm-pill">
                    Gender: {selectedGender}
                    <button onClick={() => setSelectedGender('all')}>✕</button>
                  </span>
                )}
                {selectedEthnicFilter !== 'all' && (
                  <span className="pm-pill">
                    {selectedEthnicFilter === 'true' ? 'Ethnic' : 'Western'}
                    <button onClick={() => setSelectedEthnicFilter('all')}>✕</button>
                  </span>
                )}
                {selectedFlatLayFilter !== 'all' && (
                  <span className="pm-pill">
                    {selectedFlatLayFilter === 'true' ? 'Flat Lays Only' : 'Non-Flat Lays Only'}
                    <button onClick={() => setSelectedFlatLayFilter(selectedFlatLayFilter === 'true' ? 'all' : 'true')}>✕</button>
                  </span>
                )}
                {selectedVendor !== 'all' && (
                  <span className="pm-pill">
                    Brand: {selectedVendor}
                    <button onClick={() => setSelectedVendor('all')}>✕</button>
                  </span>
                )}
                {debouncedSearch && (
                  <span className="pm-pill">
                    Search: "{debouncedSearch}"
                    <button onClick={() => setSearchTerm('')}>✕</button>
                  </span>
                )}
                {products.length > 0 && (
                  <button
                    type="button"
                    className={`pm-select-all-btn ${isAllPagesSelected ? 'all-pages' : isCurrentPageSelected ? 'page-selected' : ''}`}
                    onClick={handleSelectAllTap}
                    disabled={selectingAllPages}
                    title={
                      isAllPagesSelected
                        ? `All ${selectedProductIds.size.toLocaleString()} products across all pages selected (tap to clear)`
                        : isCurrentPageSelected && totalMatchingItems > products.length
                        ? `Current page (${products.length}) selected — tap again to select all ${totalMatchingItems.toLocaleString()} products across all pages`
                        : `First tap selects current page (${products.length}), second tap selects all pages (${totalMatchingItems.toLocaleString()})`
                    }
                  >
                    {selectingAllPages
                      ? 'Selecting all pages...'
                      : isAllPagesSelected && totalMatchingItems > products.length
                      ? `✓ Select All (${selectedProductIds.size.toLocaleString()} All Pages)`
                      : isCurrentPageSelected && totalMatchingItems > products.length
                      ? `Select All (${products.length} Page • Tap for All ${totalMatchingItems.toLocaleString()})`
                      : isCurrentPageSelected
                      ? `✓ Select All (${selectedProductIds.size})`
                      : 'Select All'}
                  </button>
                )}
                {(selectedSuperCategory || selectedCategory || selectedGender !== 'all' || selectedEthnicFilter !== 'all' || selectedFlatLayFilter !== 'true' || selectedVendor !== 'all' || debouncedSearch || selectedProductIds.size > 0) && (
                  <button
                    type="button"
                    className="pm-clear-all-btn"
                    onClick={() => {
                      if (selectedProductIds.size > 0) {
                        setSelectedProductIds(new Set());
                        setSelectAllStage('none');
                        selectAllStageRef.current = 'none';
                      } else {
                        handleClearFilters();
                      }
                    }}
                  >
                    Clear All
                  </button>
                )}
              </div>

              <div className="pm-bar-right">
                {selectedProductIds.size > 0 && (
                  <button className="pm-bulk-btn" onClick={openBulkEditModal}>
                    ⚡ Bulk Edit Category ({selectedProductIds.size.toLocaleString()})
                  </button>
                )}
                <span className="pm-results-info">
                  Showing {products.length} of {(pagination.total_items ?? totalProductsCount).toLocaleString()} products
                </span>
              </div>
            </div>
          </div>

          {/* Content Area */}
          {productsLoading ? (
            <div className="pm-loading-state">
              <div className="pm-spinner"></div>
              <p>Fetching products...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="pm-empty-state">
              <div className="pm-empty-icon">📦</div>
              <h3>No products found</h3>
              <p>Try adjusting your search query, gender filter, or selected category.</p>
              <button className="pm-btn-primary" onClick={handleClearFilters}>
                Reset All Filters
              </button>
            </div>
          ) : viewMode === 'grid' ? (
            /* ========================================================= */
            /* GRID VIEW                                                 */
            /* ========================================================= */
            <div className="pm-products-grid">
              {products.map((product, idx) => {
                const pid = product._id || product.handle || product.external_product_id;
                const isSelected = selectedProductIds.has(pid);
                const va = product.visual_attributes || {};
                const gt = va.garment_type || {};
                const catName = gt.category || 'Uncategorized';
                const superCat = gt.primary || 'Uncategorized';
                const gender = va.gender?.value || 'female';

                const isDiscontinued = Boolean(
                  product.discontinued ||
                  String(product.status || '').toLowerCase() === 'inactive' ||
                  String(product.status || '').toLowerCase() === 'discontinued'
                );
                const isTogglingThis = togglingProductId === pid;

                return (
                  <div key={pid} className={`pm-card ${isSelected ? 'selected' : ''} ${isDiscontinued ? 'is-disabled' : ''}`}>
                    <div className="pm-card-selection">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelectProduct(pid)}
                      />
                    </div>
                    <div
                      className="pm-card-img-wrap"
                      onClick={() => openGalleryModal(product, 0)}
                      style={{ cursor: 'pointer' }}
                      title="Click to view all images"
                    >
                      <span className="pm-img-seq-num" title={`Product #${idx + 1}`}>
                        #{idx + 1}
                      </span>
                      {isDiscontinued && (
                        <div className="pm-status-badge disabled" title="Product is disabled / discontinued">
                          Disabled
                        </div>
                      )}
                      {getProductImageUrl(product) ? (
                        <img
                          src={getProductImageUrl(product)}
                          alt={product.title}
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                          }}
                        />
                      ) : null}
                      <div
                        className="pm-card-no-image"
                        style={{ display: getProductImageUrl(product) ? 'none' : 'flex' }}
                      >
                        <span>No image</span>
                      </div>
                      {product.is_ethnic && (
                        <span className="pm-img-badge ethnic">Ethnic</span>
                      )}
                    </div>
                    <div className="pm-card-body">
                      <div className="pm-card-vendor">{product.vendor || 'Unknown Brand'}</div>
                      <h4
                        className="pm-card-title"
                        title={product.title}
                        onClick={() => openGalleryModal(product, 0)}
                        style={{ cursor: 'pointer' }}
                      >
                        {product.title}
                      </h4>
                      {getProductPrice(product) && (
                        <div className="pm-card-price">
                          {formatProductPrice(getProductPrice(product), getProductCurrency(product))}
                        </div>
                      )}

                      <div className="pm-card-tags">
                        <span className={`pm-tag category ${catName === 'Uncategorized' ? 'warning' : ''}`}>
                          {catName}
                        </span>
                        <span className="pm-tag super">{superCat}</span>
                        <span className="pm-tag gender">{gender === 'male' || gender === 'men' ? 'Men' : 'Women'}</span>
                      </div>

                      <div className="pm-card-actions-row">
                        <button
                          type="button"
                          className="pm-card-edit-btn"
                          onClick={() => openEditModalForProduct(product)}
                          title="Edit Category"
                        >
                          ✏️ Edit Category
                        </button>
                        <button
                          type="button"
                          className={`pm-product-toggle-btn ${isDiscontinued ? 'enable' : 'disable'}`}
                          onClick={(e) => handleToggleProductStatus(product, e)}
                          disabled={isTogglingThis}
                          title={isDiscontinued ? 'Mark this product as active' : 'Discontinue this product'}
                        >
                          {isTogglingThis ? (
                            <span className="pm-btn-inline-loader">Updating...</span>
                          ) : isDiscontinued ? (
                            <>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <path d="M20 6L9 17l-5-5" />
                              </svg>
                              <span>Mark Active</span>
                            </>
                          ) : (
                            <>
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                              </svg>
                              <span>Discontinue</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* ========================================================= */
            /* TABLE VIEW                                                */
            /* ========================================================= */
            <div className="pm-table-wrap">
              <table className="pm-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={isCurrentPageSelected}
                        onChange={toggleSelectAllPage}
                      />
                    </th>
                    <th style={{ width: '45px' }}>#</th>
                    <th style={{ width: '60px' }}>Image</th>
                    <th>Product Title</th>
                    <th>Brand / Vendor</th>
                    <th>Super Category</th>
                    <th>Category</th>
                    <th>Gender</th>
                    <th>Ethnic</th>
                    <th>Price</th>
                    <th style={{ width: '120px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, idx) => {
                    const pid = product._id || product.handle || product.external_product_id;
                    const isSelected = selectedProductIds.has(pid);
                    const va = product.visual_attributes || {};
                    const gt = va.garment_type || {};
                    const catName = gt.category || 'Uncategorized';
                    const superCat = gt.primary || 'Uncategorized';
                    const gender = va.gender?.value || 'female';
                    const imgUrl = getProductImageUrl(product);
                    const price = getProductPrice(product);

                    const isDiscontinued = Boolean(
                      product.discontinued ||
                      String(product.status || '').toLowerCase() === 'inactive' ||
                      String(product.status || '').toLowerCase() === 'discontinued'
                    );
                    const isTogglingThis = togglingProductId === pid;

                    return (
                      <tr key={pid} className={`${isSelected ? 'selected' : ''} ${isDiscontinued ? 'pm-table-row-disabled' : ''}`}>
                        <td>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelectProduct(pid)}
                          />
                        </td>
                        <td style={{ color: '#94a3b8', fontSize: '11px', fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
                          #{idx + 1}
                        </td>
                        <td onClick={() => openGalleryModal(product, 0)} style={{ cursor: 'pointer' }} title="Click to view all images">
                          {imgUrl ? (
                            <img
                              className="pm-table-thumb"
                              src={imgUrl}
                              alt=""
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="pm-table-no-img">No img</div>
                          )}
                        </td>
                        <td>
                          <div
                            className="pm-table-title"
                            title={product.title}
                            onClick={() => openGalleryModal(product, 0)}
                            style={{ cursor: 'pointer' }}
                          >
                            {product.title}
                          </div>
                          <div className="pm-table-subtitle">{product.handle || product.external_product_id}</div>
                        </td>
                        <td><span className="pm-vendor-badge">{product.vendor || '-'}</span></td>
                        <td><span className="pm-tag super">{superCat}</span></td>
                        <td>
                          <span className={`pm-tag category ${catName === 'Uncategorized' ? 'warning' : ''}`}>
                            {catName}
                          </span>
                        </td>
                        <td>
                          <span className="pm-tag gender">{gender === 'male' || gender === 'men' ? 'Men' : 'Women'}</span>
                        </td>
                        <td>
                          {product.is_ethnic ? <span className="pm-mini-tag ethnic">Ethnic</span> : <span className="pm-mini-tag">Western</span>}
                        </td>
                        <td>{price ? formatProductPrice(price, getProductCurrency(product)) : '-'}</td>
                        <td>
                          <div className="pm-table-actions-cell">
                            <button
                              className="pm-btn-table-edit"
                              onClick={() => openEditModalForProduct(product)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className={`pm-product-toggle-btn ${isDiscontinued ? 'enable' : 'disable'}`}
                              onClick={(e) => handleToggleProductStatus(product, e)}
                              disabled={isTogglingThis}
                              style={{ padding: '4px 8px', fontSize: '11px' }}
                              title={isDiscontinued ? 'Mark this product as active' : 'Discontinue this product'}
                            >
                              {isTogglingThis ? (
                                <span className="pm-btn-inline-loader">...</span>
                              ) : isDiscontinued ? (
                                'Activate'
                              ) : (
                                'Discontinue'
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Infinite Scroll Sentinel / Loading Spinner */}
          {hasMore && (
            <div ref={sentinelRef} className="pm-scroll-loader">
              <div className="pm-spinner small" />
              <span>Loading more products...</span>
            </div>
          )}

          {!hasMore && products.length > 0 && (
            <div className="pm-scroll-end">
              All {(pagination.total_items ?? products.length).toLocaleString()} products loaded
            </div>
          )}
        </main>
      </div>

      {/* ========================================================= */}
      {/* EDIT CATEGORY MODAL                                      */}
      {/* ========================================================= */}
      {editModalOpen && (
        <div className="pm-modal-overlay" onClick={() => setEditModalOpen(false)}>
          <div className="pm-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="pm-modal-header">
              <h3>
                {editingProduct ? '✏️ Edit Product Category' : `⚡ Bulk Recategorize (${selectedProductIds.size} items)`}
              </h3>
              <button className="pm-modal-close" onClick={() => setEditModalOpen(false)}>✕</button>
            </div>

            {editingProduct && (
              <div className="pm-modal-preview">
                {getProductImageUrl(editingProduct) ? (
                  <img
                    src={getProductImageUrl(editingProduct)}
                    alt=""
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="pm-modal-no-img">No img</div>
                )}
                <div className="pm-modal-preview-info">
                  <h4>{editingProduct.title}</h4>
                  <div className="pm-modal-vendor">{editingProduct.vendor}</div>
                  <div className="pm-modal-current-tags">
                    <span>Current: <strong>{editingProduct.visual_attributes?.garment_type?.category || 'Uncategorized'}</strong> ({editingProduct.visual_attributes?.garment_type?.primary || 'No Super Category'})</span>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSaveCategory} className="pm-modal-form">
              {/* Gender Selector */}
              <div className="pm-form-group">
                <label>Target Gender</label>
                <div className="pm-radio-group">
                  <label className={`pm-radio-label ${modalGender === 'women' ? 'checked' : ''}`}>
                    <input
                      type="radio"
                      name="modalGender"
                      value="women"
                      checked={modalGender === 'women'}
                      onChange={() => setModalGender('women')}
                    />
                    👩 Women
                  </label>
                  <label className={`pm-radio-label ${modalGender === 'men' ? 'checked' : ''}`}>
                    <input
                      type="radio"
                      name="modalGender"
                      value="men"
                      checked={modalGender === 'men'}
                      onChange={() => setModalGender('men')}
                    />
                    👨 Men
                  </label>
                </div>
              </div>

              {/* Super Category Selector */}
              <div className="pm-form-group">
                <label>Super Category</label>
                <select
                  className="pm-form-select"
                  value={modalSuperCategory}
                  onChange={(e) => handleModalSuperCategoryChange(e.target.value)}
                >
                  <option value="top_wear">👕 Top Wear</option>
                  <option value="bottom_wear">👖 Bottom Wear</option>
                  <option value="one_piece">👗 One Piece</option>
                  <option value="outerwear">🧥 Outerwear</option>
                  <option value="accessories">👜 Accessories</option>
                  <option value="footwear">👟 Footwear</option>
                </select>
              </div>

              {/* Specific Category Selector */}
              <div className="pm-form-group">
                <label>Category (Garment Type)</label>
                <div className="pm-cat-input-wrap">
                  <select
                    className="pm-form-select"
                    value={modalCategory}
                    onChange={(e) => handleModalCategoryChange(e.target.value)}
                  >
                    <option value="">-- Choose Category --</option>
                    {modalAvailableCategories.map(c => (
                      <option key={c.name} value={c.name}>
                        {c.name} {c.is_ethnic ? '(Ethnic)' : ''}
                      </option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="pm-form-input"
                    placeholder="Or enter custom category name..."
                    value={modalCategory}
                    onChange={(e) => setModalCategory(e.target.value)}
                  />
                </div>
              </div>

              {/* Ethnic Toggle */}
              <div className="pm-form-group pm-checkbox-group">
                <label className="pm-checkbox-label">
                  <input
                    type="checkbox"
                    checked={modalIsEthnic}
                    onChange={(e) => setModalIsEthnic(e.target.checked)}
                  />
                  <span>Mark as <strong>Ethnic Wear</strong> (e.g. Sarees, Kurta Sets, Lehengas)</span>
                </label>
              </div>

              <div className="pm-modal-actions">
                <button
                  type="button"
                  className="pm-btn-secondary"
                  onClick={() => setEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="pm-btn-primary"
                  disabled={savingCategory || !modalCategory}
                >
                  {savingCategory ? 'Saving Changes...' : 'Save & Update Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Gallery of all product images */}
      {galleryModalProduct && (
        <div className="gallery-modal-overlay" onClick={closeGalleryModal}>
          <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="gallery-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0, flexWrap: 'wrap' }}>
                <h3 className="gallery-modal-title" title={galleryModalProduct.title}>
                  {galleryModalProduct.title}
                </h3>
                {galleryModalProduct.vendor && (
                  <span className="pm-vendor-badge" style={{ fontSize: '12px' }}>
                    {galleryModalProduct.vendor}
                  </span>
                )}
                {galleryModalProduct.is_ethnic && (
                  <span className="pm-mini-tag ethnic">Ethnic</span>
                )}
              </div>
              <button
                type="button"
                className="gallery-modal-close-btn"
                onClick={closeGalleryModal}
                title="Close (Esc)"
              >
                ✕
              </button>
            </div>

            {/* Body */}
            <div className="gallery-modal-body">
              {/* Main Image View */}
              <div className="gallery-modal-main-image-wrap">
                {galleryImages[activeImageIndex]?.url ? (
                  <img
                    src={galleryImages[activeImageIndex]?.url}
                    alt={galleryModalProduct.title}
                    className="gallery-modal-main-image"
                  />
                ) : (
                  <div style={{ color: '#94a3b8' }}>No image preview</div>
                )}

                {/* Left/Right navigation arrows */}
                {galleryImages.length > 1 && (
                  <>
                    <button
                      type="button"
                      className="gallery-modal-nav-btn prev"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrevImage();
                      }}
                      title="Previous Image (←)"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="gallery-modal-nav-btn next"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleNextImage();
                      }}
                      title="Next Image (→)"
                    >
                      ›
                    </button>
                  </>
                )}

                {/* Image counter */}
                {galleryImages.length > 0 && (
                  <div className="gallery-modal-counter">
                    {activeImageIndex + 1} / {galleryImages.length}
                  </div>
                )}
              </div>
            </div>

            {/* Thumbnails reel */}
            {galleryImages.length > 1 && (
              <div className="gallery-modal-thumbnails-reel">
                {galleryImages.map((img, idx) => (
                  <div
                    key={img.id || img.url || idx}
                    className={`gallery-modal-thumb ${idx === activeImageIndex ? 'active' : ''}`}
                    onClick={() => setActiveImageIndex(idx)}
                  >
                    <img src={img.url} alt={`Thumbnail ${idx + 1}`} />
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="gallery-modal-footer">
              <div className="gallery-modal-footer-price">
                {getProductPrice(galleryModalProduct) && (
                  <span className="brand-product-price" style={{ fontSize: '18px', fontWeight: 700 }}>
                    {formatProductPrice(
                      getProductPrice(galleryModalProduct),
                      getProductCurrency(galleryModalProduct)
                    )}
                  </span>
                )}
                <div className="pm-card-tags" style={{ margin: 0, gap: '6px' }}>
                  <span className="pm-tag super">
                    {galleryModalProduct.visual_attributes?.garment_type?.primary || 'Uncategorized'}
                  </span>
                  <span className="pm-tag category">
                    {galleryModalProduct.visual_attributes?.garment_type?.category || 'Uncategorized'}
                  </span>
                  <span className="pm-tag gender">
                    {['male', 'men'].includes(galleryModalProduct.visual_attributes?.gender?.value) ? 'Men' : 'Women'}
                  </span>
                </div>

                {currentGalleryProductIndex >= 0 && (
                  <span className="gallery-modal-product-index-badge">
                    Product {currentGalleryProductIndex + 1} of {pagination.total_items || products.length}
                  </span>
                )}
              </div>

              <div className="gallery-modal-footer-actions">
                <button
                  type="button"
                  className="pm-card-edit-btn"
                  style={{ padding: '7px 14px', fontSize: '13px' }}
                  onClick={() => {
                    const prodToEdit = galleryModalProduct;
                    closeGalleryModal();
                    openEditModalForProduct(prodToEdit);
                  }}
                >
                  ✏️ Edit Category
                </button>

                {/* Discontinue / Mark Active button */}
                <button
                  type="button"
                  className={`gallery-modal-status-btn ${
                    Boolean(
                      galleryModalProduct.discontinued ||
                      String(galleryModalProduct.status || '').toLowerCase() === 'inactive' ||
                      String(galleryModalProduct.status || '').toLowerCase() === 'discontinued'
                    )
                      ? 'enable'
                      : 'disable'
                  }`}
                  onClick={() => handleToggleProductStatus(galleryModalProduct)}
                  disabled={togglingProductId === (galleryModalProduct._id || galleryModalProduct.id)}
                  title={
                    Boolean(
                      galleryModalProduct.discontinued ||
                      String(galleryModalProduct.status || '').toLowerCase() === 'inactive' ||
                      String(galleryModalProduct.status || '').toLowerCase() === 'discontinued'
                    )
                      ? 'Mark this product as active'
                      : 'Discontinue this product'
                  }
                >
                  {togglingProductId === (galleryModalProduct._id || galleryModalProduct.id) ? (
                    'Updating...'
                  ) : Boolean(
                      galleryModalProduct.discontinued ||
                      String(galleryModalProduct.status || '').toLowerCase() === 'inactive' ||
                      String(galleryModalProduct.status || '').toLowerCase() === 'discontinued'
                    ) ? (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M20 6L9 17l-5-5" />
                      </svg>
                      <span>Mark Active</span>
                    </>
                  ) : (
                    <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                      </svg>
                      <span>Discontinue</span>
                    </>
                  )}
                </button>

                {/* Product Navigation: Prev Product */}
                <button
                  type="button"
                  className="gallery-modal-prod-nav-btn prev"
                  onClick={handlePrevProduct}
                  disabled={currentGalleryProductIndex <= 0}
                  title="Switch to Previous Product (Shift + ← or [)"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                  <span>Prev Product</span>
                </button>

                {/* Product Navigation: Next Product */}
                <button
                  type="button"
                  className="gallery-modal-prod-nav-btn next"
                  onClick={handleNextProduct}
                  disabled={currentGalleryProductIndex >= products.length - 1 && !hasMore}
                  title="Switch to Next Product (Shift + → or ])"
                >
                  <span>Next Product</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>

                {/* Store link if available */}
                {(galleryModalProduct.url || galleryModalProduct.online_store_url) && (
                  <a
                    href={galleryModalProduct.url || galleryModalProduct.online_store_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="gallery-modal-store-btn"
                    title="View Product in Store"
                  >
                    <span>View Product</span>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Back to top floating button */}
      {showBackToTop && (
        <button
          type="button"
          className="pm-back-to-top"
          onClick={scrollToTop}
          aria-label="Back to top"
          title="Back to top"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="18 15 12 9 6 15" />
          </svg>
        </button>
      )}
    </div>
  );
}

export default ProductsManager;
